import React, {useCallback, useContext, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {LocaleContext} from '@pb/kit-context-locale';
import {pluralize} from '@pb/kit-formatters';

import {ManageEntitlementsHeader} from '../../components/manage-entitlements-header';
import {ManageEntitlementsHeaderButtons} from '../../components/manage-entitlements-header-buttons';
import {
    MANAGE_ENTITLEMENTS_TABS,
    ManageEntitlementsTabs,
} from '../../components/manage-entitlements-tabs';
import {
    manageEntitlementsControlHeader,
    manageEntitlementsControlHeaderSticky,
    manageEntitlementsLoadingContainer,
} from '../../manage-entitlements.module.styl';

import {NoResultMessage} from './components/no-result-page';
import {ResourceNameHeader} from './components/respource-name.header';
import {SECTION_NAME} from './constants';
import {useGetRolesGrantsResources} from './hooks/useGetRolesGrantsResources';
import {RolesGrantsResourcesView} from './roles-grants-resources.view';
import {rolesGrantsResourcesStateActions} from './slices/roles-grants-resources-state.slice';
import {rolesGrantsResourcesActions} from './slices/roles-grants-resources.slice';

import type {AccessRight} from 'src/hooks/access-rights/constants';

export const RolesGrantsResources = () => {
    const dispatch = useDispatch();
    const {locale} = useContext(LocaleContext);

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys, isLoading} = useGetRolesGrantsResources();
    const {
        isExpanded,
        disabled: isRoleExpansionDisabled,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    const filterResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(rolesGrantsResourcesStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(rolesGrantsResourcesActions.clearData());
            dispatch(rolesGrantsResourcesStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ServerResponseHandler
            serverError={loadingError}
            accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
            sectionName={SECTION_NAME}>
            <>
                <ManageEntitlementsHeader
                    title="All Existing Entitlement Roles, Grants & Resources"
                    subtitle={result?.totalCount
                        ? pluralize(`${Integer.format(result.totalCount, locale)} Result[s]`, result.totalCount)
                        : undefined}
                    buttonsBlock={<ManageEntitlementsHeaderButtons/>}
                />
                <ManageEntitlementsTabs activeTab={MANAGE_ENTITLEMENTS_TABS.ROLES_GRANTS} disabled={isLoading}/>
                <StickyElement className={`manage-controls ${manageEntitlementsControlHeaderSticky}`}>
                    <div className={manageEntitlementsControlHeader}>
                        <Group justify="space-between" alignItems="center">
                            <ResourceSuggest onSearch={filterResources} id="resource-suggest"/>
                            <Group alignItems="center">
                                <Label>Expand Roles</Label>
                                <StateButton
                                    {...{
                                        name: 'expand-roles',
                                        checked: isExpanded,
                                        disabled: isRoleExpansionDisabled,
                                        onChange: toggleRolesExpansion,
                                    }}
                                />
                            </Group>
                        </Group>
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <div className={manageEntitlementsLoadingContainer}>
                        {!!result?.data.length && (
                            <RolesGrantsResourcesView
                                dataTableId="all-entitlements"
                                expandedRoleIds={expandedRoleIds}
                                setExpandedRoleIds={setExpandedRoleIds}
                                rows={result.data}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                shortHeader={<ResourceNameHeader/>}
                                totalCount={result.totalCount || 0}
                                resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                            />
                        )}
                        <NoResultMessage
                            result={result}
                            filteredResources={cacheKeys.filters.resourceIds as string[] | undefined}
                        />
                    </div>
                </BoxLoading>
            </>
        </ServerResponseHandler>
    );
};
